/**
 * ITER170 (Feb 2026) — Auto-división FEFO cuando el usuario pide más
 * cantidad de la disponible en el lote seleccionado.
 *
 * ITER180 (Feb 2026) — Fix bug: el split ahora es unit-aware. Reconoce
 * si la línea se factura en `caja` o `unidad` y convierte
 * `cantidad_disponible` del lote (siempre en unidades) a la misma unidad
 * de la línea antes de comparar y asignar.
 *
 * Escenario: Metronidazol tabletas 500mg con upp=100.
 * - Usuario pide 40 cajas.
 * - Lote A: 1300 unidades = 13 cajas
 * - Lote B: 5000 unidades = 50 cajas
 * Antes del fix: comparaba 40 <= 1300 → no dividía.
 * Después del fix: convierte a cajas → 40 > 13 → divide 13 A + 27 B.
 */

/** Determina si la línea se factura por caja y el factor unidades/caja. */
function _lineaConfigUnidad(linea) {
  const esCaja = (linea?.unidad_venta || "").toLowerCase() === "caja";
  const upc = Math.max(1, parseInt(linea?.unidades_por_caja) || 1);
  return { esCaja, upc };
}

/** Convierte cantidad_disponible del lote (en unidades) a la unidad de la línea. */
function _dispEnUnidadLinea(loteCantDisp, esCaja, upc) {
  const disp = Number(loteCantDisp) || 0;
  if (!esCaja) return disp;
  // En cajas: sólo cajas COMPLETAS (no fracciones)
  return Math.floor(disp / upc);
}

/**
 * Calcula el plan de división FEFO para una línea.
 * @param {object} linea línea actual (debe tener _lotes_disponibles + lote_id + cantidad + unidad_venta + unidades_por_caja)
 * @param {number} cantidadDeseada cantidad total que el usuario quiere despachar (en la MISMA unidad de la línea)
 * @returns {null | {plan: Array<{lote_id, numero_lote, fecha_vencimiento, cantidad_a_tomar, cantidad_disponible, unidad}>, faltante: number, cantidad_deseada: number, cantidad_asignada: number, unidad: 'caja'|'unidad'}}
 *   null si no hay overflow / no hay lotes o el lote actual alcanza.
 */
export function computeFEFOSplit(linea, cantidadDeseada) {
  const lotes = Array.isArray(linea?._lotes_disponibles) ? linea._lotes_disponibles : [];
  const cant = Number(cantidadDeseada) || 0;
  if (cant <= 0) return null;
  const { esCaja, upc } = _lineaConfigUnidad(linea);
  const loteActual = lotes.find(x => x.id === linea.lote_id);
  // ITER182 (Feb 2026): si loteActual no está en la lista (fue reservado
  // por completo por otros pedidos y quedó con efectivo=0), lo tratamos
  // como disponibilidad=0 y forzamos redistribución sobre los demás.
  const dispActualEnLinea = loteActual
    ? _dispEnUnidadLinea(loteActual.cantidad_disponible, esCaja, upc)
    : 0;
  if (cant <= dispActualEnLinea) return null; // ok, cabe en el lote actual
  if (lotes.length === 0 && !loteActual) return null; // nada que sugerir

  // Ordenar lotes por FEFO. Si el actual sigue disponible, va primero
  // (respetar la decisión del usuario); si ya no existe, secuencia
  // empieza por los alternos ordenados por vencimiento ascendente.
  const otros = lotes
    .filter(x => x.id !== linea.lote_id)
    .sort((a, b) => (a.fecha_vencimiento || "9999-12-31").localeCompare(b.fecha_vencimiento || "9999-12-31"));
  const secuencia = loteActual ? [loteActual, ...otros] : otros;
  if (secuencia.length === 0) return null;

  const plan = [];
  let restante = cant;
  for (const lt of secuencia) {
    const dispEnLinea = _dispEnUnidadLinea(lt.cantidad_disponible, esCaja, upc);
    if (dispEnLinea <= 0) continue;
    const toma = Math.min(restante, dispEnLinea);
    if (toma > 0) {
      plan.push({
        lote_id: lt.id,
        numero_lote: lt.numero_lote || "",
        fecha_elaboracion: lt.fecha_elaboracion || "",
        fecha_vencimiento: lt.fecha_vencimiento || "",
        cantidad_a_tomar: toma,
        cantidad_disponible: dispEnLinea,
        // Meta útil para el UI
        unidad: esCaja ? "caja" : "unidad",
        cantidad_disponible_unidades: Number(lt.cantidad_disponible) || 0,
      });
      restante -= toma;
      if (restante <= 0) break;
    }
  }

  const asignada = plan.reduce((s, p) => s + p.cantidad_a_tomar, 0);
  return {
    plan,
    faltante: Math.max(0, cant - asignada),
    cantidad_deseada: cant,
    cantidad_asignada: asignada,
    unidad: esCaja ? "caja" : "unidad",
  };
}

/**
 * Aplica el plan de división FEFO al array de líneas. Devuelve un NUEVO
 * array con la línea `idx` mutada (usa el primer lote del plan) y una
 * línea adicional por cada tramo restante (misma info del producto, otro
 * lote, otra cantidad).
 *
 * @param {Array} lineas array de líneas actuales
 * @param {number} idx índice de la línea a dividir
 * @param {object} splitResult resultado de computeFEFOSplit
 * @param {(l:object)=>object} recalcSubtotal función para recalcular el subtotal (viene de SalesLineRow)
 * @param {()=>string} generateLineId función para generar _lineId único
 */
export function applyFEFOSplit(lineas, idx, splitResult, recalcSubtotal, generateLineId) {
  const nuevas = [...lineas];
  const orig = nuevas[idx];
  if (!orig || !splitResult || !splitResult.plan.length) return nuevas;
  const plan = splitResult.plan;

  // Primera línea: reasignar (cantidad en la MISMA unidad de la línea original)
  nuevas[idx] = recalcSubtotal({
    ...orig,
    lote_id: plan[0].lote_id,
    numero_lote: plan[0].numero_lote,
    fecha_elaboracion: plan[0].fecha_elaboracion,
    fecha_vencimiento: plan[0].fecha_vencimiento,
    cantidad: plan[0].cantidad_a_tomar,
  });

  // Líneas adicionales: una por cada lote del plan (index 1+)
  const extras = plan.slice(1).map(p => recalcSubtotal({
    ...orig,
    _lineId: generateLineId(),
    lote_id: p.lote_id,
    numero_lote: p.numero_lote,
    fecha_elaboracion: p.fecha_elaboracion,
    fecha_vencimiento: p.fecha_vencimiento,
    cantidad: p.cantidad_a_tomar,
    // Marcar como generada automáticamente (opcional, uso informativo)
    _auto_split_from: idx,
  }));

  // Insertar extras justo después del idx
  nuevas.splice(idx + 1, 0, ...extras);
  return nuevas;
}

/** Genera un _lineId único basado en tiempo + random (formato existente en el proyecto). */
export function makeLineId() {
  return `l-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}
